import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IAPIItemResult,
  IPagination,
  IPaginationResult,
  IRequestResult,
  IResult,
} from "../../../../../common/interfaces";
import { environment as env } from "../../../../../../environments/environment.prod";
import FrequenciesAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route-frequencies/frequencies.entity";
import { AbstractService } from "app/services/abstract.service";
import { IUpdateSubRoute } from "../interfaces/update-sub-route.interface";
import { ICreateSubRouteFrequency } from "./interfaces/create-sub-route-frequency.interface";
import { AppointmentAttributes, SubRouteAttributes } from "@albatrosdeveloper/ave-models-package";

const apiUrl = env.API_ROUTING_URL;
const moduleName = "sub_route_frequencies";
const appointmentModuleName = "appointment"

/**
 * Shape of the response returned by
 * `GET /api/sub_route_frequencies/weekday_clients_count/:subRouteId`.
 * Each key is a weekday in English and the value is how many clients are
 * configured on that day for the sub route.
 */
export interface WeekdayClientsCount {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

@Injectable({
  providedIn: "root",
})
export class SubRouteFrequenciesService extends AbstractService<
FrequenciesAttributes,
ICreateSubRouteFrequency,
  IUpdateSubRoute
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findBySubRouteId(subRouteId: string): Observable<IResult<FrequenciesAttributes>> {
    const API_URL: string = `${apiUrl}api/${moduleName}/${subRouteId}`;
    return this.http.get<IResult<FrequenciesAttributes>>(API_URL);
  }

  /**
   * Summary of how many clients are configured for each weekday on a sub
   * route. Used by the frequency modal when weekday mode is on, so the panel
   * header can show the count without expanding the day.
   */
  findWeekdayClientsCount(subRouteId: string): Observable<IAPIItemResult<WeekdayClientsCount>> {
    const API_URL: string = `${apiUrl}api/${moduleName}/weekday_clients_count/${subRouteId}`;
    return this.http.get<IAPIItemResult<WeekdayClientsCount>>(API_URL);
  }

  findAppointmentById(appointmentId:string){
    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/byId/${appointmentId}`;
    return this.http.get<IAPIItemResult<AppointmentAttributes>>(API_URL);
  }

  findAppointmentWithCount(subRoute: SubRouteAttributes, date): Observable<IPaginationResult<AppointmentAttributes>> {
    const query = buildQuery<AppointmentAttributes>(
      where('type', Ops.eq('C')),
      andWhere(<any>'subRoute.id', Ops.eq(subRoute.id)),
      //where('company._id', Ops.eq(this.selectedSalesOffice?.company?._id?.toString(), Normalizers.ObjectId)),
      andWhere('statusId', Ops.eq(1)),
      andWhere("date", Ops.btwnDates(date.start, date.end)),
      orderBy('date', OrderVariant.Asc)
    );
    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<AppointmentAttributes>>(API_URL)
  }
  
  createManyAppointment(data: AppointmentAttributes){
    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/`
    return this.http.post<IRequestResult<AppointmentAttributes>>(API_URL, data);
  }

  deleteCompleteBusinessPartnerAppointment(appointmentId: string){
    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/${appointmentId}`
    return this.http.delete<IRequestResult<AppointmentAttributes>>(API_URL);
  }

  deleteManyCompleteBusinessPartnerAppointment(routeId: string, businessPartnerId: string){
    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/delete_bp_by_subroute/${routeId}/${businessPartnerId}`
    return this.http.delete<IRequestResult<AppointmentAttributes>>(API_URL);
  }

  deleteBusinessPartnerAppointment(appointmentId:string, bpIds){
    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/delete_bps/${appointmentId}`
    return this.http.patch<IRequestResult<AppointmentAttributes>>(API_URL, bpIds);
  }

  createBusinessPartnerAppointment(appointmentId:string, bpIds){
    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/add_bps/${appointmentId}`
    return this.http.patch<IRequestResult<AppointmentAttributes>>(API_URL, bpIds);
  }

  findAppointmentWithCountByBusiness(
    businessPartnerId: string,
    subRouteId: string,
    date?: Date
  ): Observable<IPaginationResult<AppointmentAttributes>> {
    let arrayQueries = []
    if (date) {
      const startDate = new Date(date);
      startDate.setHours(0, 0, 0, 0);
      arrayQueries.push(andAllWhere('date', Ops.gte(startDate)));
    }
    const query = buildQuery<AppointmentAttributes>(
      where("type", Ops.eq('C')),
      andAllWhere("statusId", Ops.eq(1)),
      andAllWhere('subRoute.id' as keyof AppointmentAttributes, Ops.eq(subRouteId)),
      andAllWhere('businessPartners.businessPartner.id' as keyof AppointmentAttributes, Ops.eq(businessPartnerId)),
      ...arrayQueries,
      orderBy("date", OrderVariant.Asc)
    );

    const API_URL: string = `${apiUrl}api/${appointmentModuleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<AppointmentAttributes>>(API_URL)
  }
}
